import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, User, Car, CreditCard, FileText, Loader2, Ban, Unlock,
  CheckCircle, XCircle, Image, History, DollarSign, Edit2, ChevronDown, ChevronUp,
  Phone, Mail, MapPin, Globe, Calendar, Hash, Palette, Users, Briefcase,
  Star, Shield, Send, Clock, AlertCircle, Eye, Download, Upload, Camera, Trash2, X, KeyRound, Copy
} from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { DriverVehiclesTab } from '../components/admin/DriverVehiclesTab';
import { DriverPayoutsTab } from '../components/admin/DriverPayoutsTab';
import { formatPickupDate } from '../utils/datetime';

const DRIVER_STATUS = {
  approved: { label: 'Aktiv', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  blocked: { label: 'Gesperrt', cls: 'bg-red-100 text-red-700 border-red-200' },
  rejected: { label: 'Abgelehnt', cls: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const VEHICLE_STATUS = {
  approved: { label: 'Freigegeben', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Ausstehend', cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' },
};

const TYPE_LABELS = { economy: 'Economy', comfort: 'Comfort', business: 'Business', premium: 'Premium', vip: 'VIP', suv: 'SUV', van: 'Van', minibus: 'Kleinbus', bus: 'Bus' };

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', bidding: 'bg-amber-100 text-amber-700',
  booked: 'bg-emerald-100 text-emerald-700', completed: 'bg-slate-200 text-slate-600',
  cancelled: 'bg-red-100 text-red-700', pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700',
};
const STATUS_LABELS = {
  open: 'Offen', bidding: 'Angebote', booked: 'Gebucht', completed: 'Abgeschlossen',
  cancelled: 'Storniert', pending: 'Ausstehend', accepted: 'Akzeptiert', rejected: 'Abgelehnt',
};

const TABS = [
  { id: 'info', label: 'Persönliche Daten', icon: User },
  { id: 'vehicles', label: 'Fahrzeuge', icon: Car },
  { id: 'bids', label: 'Gebote', icon: Send },
  { id: 'transfers', label: 'Erhaltene Transfers', icon: CheckCircle },
  { id: 'finance', label: 'Finanzen', icon: DollarSign },
  { id: 'payouts', label: 'Auszahlungen', icon: CreditCard },
  { id: 'driver-email', label: 'E-Mail', icon: Mail },
  { id: 'driver-sms', label: 'SMS', icon: Phone },
];

export default function AdminDriverDetail() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [form, setForm] = useState({});

  // Vehicle editing
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({});
  const [vehicleAction, setVehicleAction] = useState(null);
  const [expandedVehicle, setExpandedVehicle] = useState(null);

  // Transfers/Bids
  const [transfers, setTransfers] = useState(null);
  const [loadingTransfers, setLoadingTransfers] = useState(false);

  // Earnings
  const [earnings, setEarnings] = useState(null);

  // Set-password modal state (for Google-login drivers who need mobile app access)
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved, setPwdSaved] = useState(false);

  const generatePwd = () => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const digits = '23456789';
    const pick = (s, n) => Array.from({ length: n }, () => s[Math.floor(Math.random() * s.length)]).join('');
    return pick(upper, 1) + pick(lower, 3) + pick(digits, 4);
  };

  const openPwdModal = () => {
    setNewPwd(generatePwd());
    setPwdSaved(false);
    setPwdModalOpen(true);
  };

  const savePassword = async () => {
    if (!newPwd || newPwd.length < 6) { alert('Passwort muss mindestens 6 Zeichen haben.'); return; }
    const userId = data?.user?.id;
    if (!userId) { alert('User-ID nicht gefunden.'); return; }
    if (!window.confirm(`Passwort für ${data?.user?.email} jetzt setzen?`)) return;
    setPwdSaving(true);
    try {
      await api.put(`/admin/user/${userId}/password`, { password: newPwd });
      setPwdSaved(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Fehler beim Setzen des Passworts');
    } finally { setPwdSaving(false); }
  };

  const copyPwd = async () => {
    try { await navigator.clipboard.writeText(newPwd); alert('Passwort kopiert.'); } catch { /* ignore */ }
  };
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  // Payouts
  const [payoutForm, setPayoutForm] = useState({ amount: '', method: 'paypal', notes: '' });
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [paypalConfirm, setPaypalConfirm] = useState(false);
  const [checkingPayoutStatus, setCheckingPayoutStatus] = useState(null);

  // Auth guard
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const loadDriver = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/driver/${driverId}/full`);
      setData(res.data);
      initForm(res.data);
    } catch {
      setMessage({ text: 'Fehler beim Laden der Fahrerdaten', type: 'error' });
    } finally { setLoading(false); }
  }, [driverId]);

  useEffect(() => { loadDriver(); }, [loadDriver]);

  const initForm = (d) => {
    const drv = d.driver || {};
    const usr = d.user || {};
    setForm({
      name: usr.name || '', email: usr.email || '', phone: usr.phone || '',
      region: drv.region || '', country: drv.country || '',
      paypal_email: drv.paypal_email || '',
      instapay_number: drv.instapay_number || '',
      vodafone_cash_number: drv.vodafone_cash_number || '',
      languages: (drv.languages || []).join(', '),
    });
  };

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (typeof payload.languages === 'string') {
        payload.languages = payload.languages.split(',').map(l => l.trim()).filter(Boolean);
      }
      await api.put(`/admin/driver/${driverId}/edit`, payload);
      showMsg('Profil gespeichert');
      setEditing(false);
      await loadDriver();
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally { setSaving(false); }
  };

  const handleDriverAction = async (action) => {
    try {
      await api.post(`/admin/${action}-driver/${driverId}`);
      showMsg(action === 'approve' ? 'Fahrer genehmigt' : action === 'reject' ? 'Fahrer abgelehnt' : action === 'block' ? 'Fahrer gesperrt' : 'Fahrer entsperrt');
      await loadDriver();
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Aktion fehlgeschlagen', 'error');
    }
  };

  // Vehicle actions
  const handleVehicleStatus = async (vehicleId, newStatus) => {
    setVehicleAction(vehicleId);
    try {
      await api.put(`/admin/driver/${driverId}/vehicle/${vehicleId}/status`, { status: newStatus });
      showMsg(`Fahrzeug ${newStatus === 'approved' ? 'freigegeben' : 'abgelehnt'}`);
      await loadDriver();
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Fehler', 'error');
    } finally { setVehicleAction(null); }
  };

  const startEditVehicle = (v) => {
    setEditingVehicle(v.id);
    setVehicleForm({
      make: v.make || '', model: v.model || '', year: v.year || '',
      color: v.color || '', plate_number: v.plate_number || '',
      vehicle_type: v.vehicle_type || '', passengers: v.passengers || 4, luggage: v.luggage || 3,
    });
  };

  const saveVehicle = async (vehicleId) => {
    setSaving(true);
    try {
      const payload = { ...vehicleForm };
      if (payload.year) payload.year = Number(payload.year);
      if (payload.passengers) payload.passengers = Number(payload.passengers);
      if (payload.luggage) payload.luggage = Number(payload.luggage);
      await api.put(`/admin/driver/${driverId}/vehicle/${vehicleId}/edit`, payload);
      showMsg('Fahrzeugdaten gespeichert');
      setEditingVehicle(null);
      await loadDriver();
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Fehler', 'error');
    } finally { setSaving(false); }
  };

  // Load transfers & bids
  const loadTransfers = useCallback(async () => {
    if (transfers) return;
    setLoadingTransfers(true);
    try {
      const res = await api.get(`/admin/driver/${driverId}/transfers`);
      setTransfers(res.data);
    } catch { showMsg('Fehler beim Laden der Transfers', 'error'); }
    finally { setLoadingTransfers(false); }
  }, [driverId, transfers]);

  // Load earnings
  const loadEarnings = useCallback(async () => {
    setLoadingEarnings(true);
    try {
      const res = await api.get(`/admin/driver/${driverId}/earnings`);
      setEarnings(res.data);
    } catch { showMsg('Fehler beim Laden der Einnahmen', 'error'); }
    finally { setLoadingEarnings(false); }
  }, [driverId]);

  // Tab change handler
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if ((tabId === 'bids' || tabId === 'transfers') && !transfers) loadTransfers();
    if ((tabId === 'finance' || tabId === 'payouts') && !earnings) loadEarnings();
  };

  // Payout
  const handlePayout = async (autoPaypal = false) => {
    if (!payoutForm.amount || Number(payoutForm.amount) <= 0) return;
    setPayoutSaving(true);
    setPaypalConfirm(false);
    try {
      const res = await api.post(`/admin/driver/${driverId}/payout`, {
        amount: Number(payoutForm.amount), method: payoutForm.method, notes: payoutForm.notes,
        auto_paypal: autoPaypal,
      });
      showMsg(res.data.status === 'processing' ? 'PayPal-Auszahlung wird verarbeitet' : 'Auszahlung erfolgreich markiert');
      setPayoutForm({ amount: '', method: 'paypal', notes: '' });
      await loadEarnings();
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Fehler bei der Auszahlung', 'error');
    } finally { setPayoutSaving(false); }
  };

  const handleCheckPayoutStatus = async (payoutId) => {
    setCheckingPayoutStatus(payoutId);
    try {
      const res = await api.get(`/admin/payout/${payoutId}/status`);
      await loadEarnings();
      const ppStatus = res.data.paypal_status;
      if (ppStatus) showMsg(`PayPal Status: ${ppStatus.batch_status || ppStatus.item_status || 'Unbekannt'}`);
    } catch {
      showMsg('Status-Abfrage fehlgeschlagen', 'error');
    } finally { setCheckingPayoutStatus(null); }
  };

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const response = await api.get(`/admin/driver/${driverId}/finance/export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Finanzen_${(data?.user?.name || 'Fahrer').replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showMsg('CSV exportiert');
    } catch {
      showMsg('Export fehlgeschlagen', 'error');
    }
  };

  // PDF Export
  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/admin/driver/${driverId}/finance/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Finanzen_${(data?.user?.name || 'Fahrer').replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      showMsg('PDF exportiert');
    } catch {
      showMsg('PDF-Export fehlgeschlagen', 'error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
          <p className="text-slate-600">Fahrerdaten konnten nicht geladen werden.</p>
          <button onClick={() => navigate('/admin')} className="mt-4 text-orange-500 hover:underline text-sm">Zurück zum Dashboard</button>
        </div>
      </div>
    );
  }

  const drv = data.driver || {};
  const usr = data.user || {};
  const docs = drv.documents || {};
  const vehicles = drv.vehicles || [];
  const status = drv.status || 'pending';
  const statusInfo = DRIVER_STATUS[status] || DRIVER_STATUS.pending;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-driver-detail-page">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors" data-testid="back-to-admin">
              <ArrowLeft size={18} /> <span className="text-sm font-medium">Admin</span>
            </button>
            <div className="h-5 w-px bg-slate-200" />
            <a href="/" className="flex items-baseline gap-0.5">
              <span className="text-xl font-extrabold text-orange-500">366</span>
              <span className="text-xs font-semibold text-slate-800">Transfer</span>
            </a>
          </div>
          <span className="text-xs text-slate-400">Fahrer-Verwaltung</span>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Message */}
        {message && (
          <div className={`mb-4 p-3 rounded-xl text-sm font-medium ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="page-message">
            {message.text}
          </div>
        )}

        {/* Driver Header Card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6" data-testid="driver-header-card">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-orange-500 flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {usr.name?.charAt(0)?.toUpperCase() || 'F'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-800" data-testid="driver-name">{usr.name || 'Unbekannt'}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusInfo.cls}`} data-testid="driver-status">
                  {statusInfo.label}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                {usr.email && <span className="flex items-center gap-1.5"><Mail size={14} className="text-slate-400" /> {usr.email}</span>}
                {usr.phone && <span className="flex items-center gap-1.5"><Phone size={14} className="text-slate-400" /> {usr.phone}</span>}
                {drv.region && <span className="flex items-center gap-1.5"><MapPin size={14} className="text-slate-400" /> {drv.region}</span>}
                {drv.languages?.length > 0 && <span className="flex items-center gap-1.5"><Globe size={14} className="text-slate-400" /> {drv.languages.join(', ')}</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs text-slate-400">
                <span>ID: {drv.id?.slice(0, 8)}...</span>
                <span>Registriert: {drv.created_at ? new Date(drv.created_at).toLocaleDateString('de-DE') : '-'}</span>
                <span>Bewertung: {drv.rating || 5.0}/5.0</span>
                <span>Fahrzeuge: {vehicles.length}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 shrink-0">
              {status === 'pending' && (
                <>
                  <button onClick={() => handleDriverAction('approve')} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors" data-testid="approve-driver-btn">
                    <CheckCircle size={14} /> Genehmigen
                  </button>
                  <button onClick={() => handleDriverAction('reject')} className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors" data-testid="reject-driver-btn">
                    <XCircle size={14} /> Ablehnen
                  </button>
                </>
              )}
              {status === 'approved' && (
                <button onClick={() => handleDriverAction('block')} className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors" data-testid="block-driver-btn">
                  <Ban size={14} /> Sperren
                </button>
              )}
              {status === 'blocked' && (
                <button onClick={() => handleDriverAction('unblock')} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors" data-testid="unblock-driver-btn">
                  <Unlock size={14} /> Entsperren
                </button>
              )}
              <a href={`/driver/public/${driverId}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors" data-testid="view-public-profile">
                <Eye size={14} /> Öffentliches Profil
              </a>
              <button onClick={openPwdModal} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-medium hover:bg-indigo-100 transition-colors" data-testid="set-driver-password-btn" title="Passwort für Mobile App setzen">
                <KeyRound size={14} /> Passwort setzen
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="border-b border-slate-100 flex overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              let count = '';
              if (tab.id === 'vehicles') count = ` (${vehicles.length})`;
              if (tab.id === 'bids' && transfers) count = ` (${transfers.total_bids})`;
              if (tab.id === 'transfers' && transfers) count = ` (${transfers.total_won})`;
              return (
                <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-orange-500 text-orange-600 bg-orange-50/50' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`tab-${tab.id}`}>
                  <Icon size={16} /> {tab.label}{count}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* ===== TAB: Personal Info ===== */}
            {activeTab === 'info' && (
              <div data-testid="tab-content-info">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-slate-800">Persönliche Daten</h2>
                  {!editing ? (
                    <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors" data-testid="edit-personal-btn">
                      <Edit2 size={14} /> Bearbeiten
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditing(false); initForm(data); }} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Abbrechen</button>
                      <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition-colors" data-testid="save-personal-btn">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    { label: 'Name', field: 'name', icon: User, value: usr.name },
                    { label: 'E-Mail', field: 'email', icon: Mail, type: 'email', value: usr.email },
                    { label: 'Telefon', field: 'phone', icon: Phone, type: 'tel', value: usr.phone },
                    { label: 'Land', field: 'country', icon: Globe, value: drv.country },
                    { label: 'Region / Stadt', field: 'region', icon: MapPin, value: drv.region },
                    { label: 'Sprachen', field: 'languages', icon: Globe, value: (drv.languages || []).join(', ') },
                    { label: 'PayPal E-Mail', field: 'paypal_email', icon: CreditCard, type: 'email', value: drv.paypal_email },
                    { label: 'InstaPay Nummer', field: 'instapay_number', icon: CreditCard, value: drv.instapay_number },
                    { label: 'Vodafone Cash', field: 'vodafone_cash_number', icon: Phone, value: drv.vodafone_cash_number },
                  ].map(f => {
                    const FIcon = f.icon;
                    return (
                      <div key={f.field} className="bg-slate-50 rounded-xl p-4" data-testid={`field-${f.field}`}>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-2">
                          <FIcon size={12} /> {f.label}
                        </label>
                        {editing ? (
                          <input type={f.type || 'text'} value={form[f.field] || ''}
                            onChange={e => setForm(prev => ({ ...prev, [f.field]: e.target.value }))}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                            data-testid={`input-${f.field}`} />
                        ) : (
                          <p className="text-sm text-slate-800 font-medium" data-testid={`value-${f.field}`}>{f.value || '-'}</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Non-editable info */}
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Calendar size={12} /> Registriert am</span>
                    <p className="text-sm text-slate-800 font-medium mt-1">{drv.created_at ? new Date(drv.created_at).toLocaleDateString('de-DE') : '-'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Star size={12} /> Bewertung</span>
                    <p className="text-sm text-slate-800 font-medium mt-1">{drv.rating || 5.0} / 5.0</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Shield size={12} /> Fahrer-ID</span>
                    <p className="text-sm text-slate-800 font-medium mt-1 break-all">{drv.id}</p>
                  </div>
                </div>

                {/* Personal Documents */}
                <PersonalDocsSection docs={docs} driverId={drv.id} />
              </div>
            )}

            {/* ===== TAB: Vehicles ===== */}
            {activeTab === 'vehicles' && (
              <DriverVehiclesTab
                vehicles={vehicles}
                expandedVehicle={expandedVehicle}
                setExpandedVehicle={setExpandedVehicle}
                editingVehicle={editingVehicle}
                vehicleForm={vehicleForm}
                setVehicleForm={setVehicleForm}
                startEditVehicle={startEditVehicle}
                saveVehicle={saveVehicle}
                handleVehicleStatus={handleVehicleStatus}
                vehicleAction={vehicleAction}
                saving={saving}
                setEditingVehicle={setEditingVehicle}
                driverId={drv.id}
              />
            )}

            {/* ===== TAB: E-Mail ===== */}
            {activeTab === 'driver-email' && (
              <DriverEmailTab driverId={drv.id} driverName={usr.name} driverEmail={usr.email} driverLanguage={usr.language || drv.languages?.[0] || 'de'} docs={docs} vehicles={vehicles} />
            )}

            {/* ===== TAB: SMS ===== */}
            {activeTab === 'driver-sms' && (
              <DriverSmsTab driverName={usr.name} driverPhone={usr.phone} />
            )}

            {/* ===== TAB: Bids (All offers) ===== */}
            {activeTab === 'bids' && (
              <div data-testid="tab-content-bids">
                <h2 className="text-lg font-bold text-slate-800 mb-5">Alle Gebote des Fahrers</h2>
                {loadingTransfers ? (
                  <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-orange-500" /></div>
                ) : transfers?.bids?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left">
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Kunde</th>
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Strecke</th>
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Datum</th>
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Preis</th>
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Gebot-Status</th>
                          <th className="px-4 py-3 text-xs font-semibold text-slate-500">Transfer-Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transfers.bids.map(o => (
                          <tr key={o.id} className="hover:bg-slate-50" data-testid={`bid-row-${o.id}`}>
                            <td className="px-4 py-3 font-medium text-slate-800">{o.transfer?.customer_name || '-'}</td>
                            <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{o.transfer?.pickup} → {o.transfer?.dropoff}</td>
                            <td className="px-4 py-3 text-slate-500">{formatPickupDate(o.transfer?.pickup_date) || '-'}</td>
                            <td className="px-4 py-3 font-bold text-slate-800">{o.price} {o.currency}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-600'}`}>
                                {STATUS_LABELS[o.status] || o.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[o.transfer?.status] || 'bg-slate-100 text-slate-600'}`}>
                                {STATUS_LABELS[o.transfer?.status] || o.transfer?.status || '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400"><Send size={40} className="mx-auto mb-3 text-slate-200" /><p>Keine Gebote abgegeben</p></div>
                )}
              </div>
            )}

            {/* ===== TAB: Won Transfers ===== */}
            {activeTab === 'transfers' && (
              <div data-testid="tab-content-transfers">
                <h2 className="text-lg font-bold text-slate-800 mb-5">Erhaltene Transfers</h2>
                {loadingTransfers ? (
                  <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-orange-500" /></div>
                ) : transfers?.won?.length > 0 ? (
                  <div className="space-y-3">
                    {transfers.won.map(o => (
                      <div key={o.id} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid={`won-transfer-${o.id}`}>
                        <div>
                          <p className="font-semibold text-slate-800">{o.transfer?.customer_name || '-'}</p>
                          <p className="text-sm text-slate-500 mt-0.5">{o.transfer?.pickup} → {o.transfer?.dropoff}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatPickupDate(o.transfer?.pickup_date) || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-700">{o.price} {o.currency}</p>
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[o.transfer?.status] || 'bg-slate-100 text-slate-600'}`}>
                            {STATUS_LABELS[o.transfer?.status] || o.transfer?.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400"><CheckCircle size={40} className="mx-auto mb-3 text-slate-200" /><p>Keine erhaltenen Transfers</p></div>
                )}
              </div>
            )}

            {/* ===== TAB: Finance ===== */}
            {activeTab === 'finance' && (
              <div data-testid="tab-content-finance">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-slate-800">Finanzen</h2>
                  <div className="flex gap-2">
                    <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors" data-testid="export-pdf-btn">
                      <FileText size={14} /> PDF Export
                    </button>
                    <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-4 py-2 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors" data-testid="export-csv-btn">
                      <Download size={14} /> CSV Export
                    </button>
                  </div>
                </div>
                {loadingEarnings ? (
                  <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-orange-500" /></div>
                ) : earnings ? (
                  <div className="space-y-6">
                    {/* Summary Cards: Brutto / Kommission / Netto / Bereits ausgezahlt / Offen */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <p className="text-xs text-blue-600 font-medium mb-1">Brutto verdient</p>
                        <p className="text-xl font-bold text-blue-800" data-testid="total-gross">{(earnings.total_gross ?? earnings.total_earned) || 0} EUR</p>
                      </div>
                      <div className="bg-rose-50 rounded-xl p-4 border border-rose-100">
                        <p className="text-xs text-rose-600 font-medium mb-1">Portal-Kommission ({earnings.commission_percent ?? 15}%)</p>
                        <p className="text-xl font-bold text-rose-800" data-testid="total-commission">−{earnings.total_commission ?? 0} EUR</p>
                      </div>
                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                        <p className="text-xs text-indigo-600 font-medium mb-1">Netto-Anspruch</p>
                        <p className="text-xl font-bold text-indigo-800" data-testid="total-net">{earnings.total_net ?? 0} EUR</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                        <p className="text-xs text-emerald-600 font-medium mb-1">Bereits ausgezahlt</p>
                        <p className="text-xl font-bold text-emerald-800" data-testid="total-paid">{earnings.total_paid} EUR</p>
                      </div>
                      <div className={`rounded-xl p-4 border ${earnings.balance_owed > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                        <p className={`text-xs font-medium mb-1 ${earnings.balance_owed > 0 ? 'text-orange-600' : 'text-slate-500'}`}>Offener Betrag (Netto)</p>
                        <p className={`text-xl font-bold ${earnings.balance_owed > 0 ? 'text-orange-800' : 'text-slate-600'}`} data-testid="balance-owed">{earnings.balance_owed} EUR</p>
                      </div>
                    </div>

                    {/* Transfer List */}
                    {earnings.transfers?.length > 0 ? (
                      <div>
                        <h3 className="text-sm font-bold text-slate-700 mb-3">Alle Transfers mit Preisen ({earnings.transfers.length})</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 text-left">
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500">Kunde</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500">Strecke</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500">Datum</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 text-right">Brutto</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 text-right">Kommission</th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 text-right">Netto</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {earnings.transfers.map(t => (
                                <tr key={t.offer_id} className="hover:bg-slate-50" data-testid={`finance-row-${t.offer_id}`}>
                                  <td className="px-4 py-3 font-medium text-slate-800">{t.customer_name || '-'}</td>
                                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{t.pickup} → {t.dropoff}</td>
                                  <td className="px-4 py-3 text-slate-500">{formatPickupDate(t.pickup_date) || '-'}</td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || 'bg-slate-100 text-slate-600'}`}>
                                      {STATUS_LABELS[t.status] || t.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium text-slate-800">{t.price} {t.currency}</td>
                                  <td className="px-4 py-3 text-right text-rose-600">−{t.commission ?? 0} {t.currency}</td>
                                  <td className="px-4 py-3 text-right font-bold text-emerald-700">{t.net ?? t.price} {t.currency}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-slate-50 font-bold">
                                <td colSpan="4" className="px-4 py-3 text-sm text-slate-700">Gesamt</td>
                                <td className="px-4 py-3 text-right text-sm text-slate-800">{earnings.total_gross ?? earnings.total_earned} EUR</td>
                                <td className="px-4 py-3 text-right text-sm text-rose-700">−{earnings.total_commission ?? 0} EUR</td>
                                <td className="px-4 py-3 text-right text-sm text-emerald-700">{earnings.total_net ?? 0} EUR</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400"><DollarSign size={40} className="mx-auto mb-3 text-slate-200" /><p>Noch keine Transfers</p></div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">Daten werden geladen...</div>
                )}
              </div>
            )}

            {/* ===== TAB: Payouts ===== */}
            {activeTab === 'payouts' && (
              <DriverPayoutsTab
                earnings={earnings}
                loadingEarnings={loadingEarnings}
                driver={drv}
                user={usr}
                payoutForm={payoutForm}
                setPayoutForm={setPayoutForm}
                payoutSaving={payoutSaving}
                paypalConfirm={paypalConfirm}
                setPaypalConfirm={setPaypalConfirm}
                handlePayout={handlePayout}
                checkingPayoutStatus={checkingPayoutStatus}
                handleCheckPayoutStatus={handleCheckPayoutStatus}
                handleExportCSV={handleExportCSV}
                handleExportPDF={handleExportPDF}
              />
            )}
          </div>
        </div>
      </div>

      {/* Set Password Modal */}
      {pwdModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPwdModalOpen(false)} data-testid="set-password-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KeyRound size={18} className="text-indigo-600" />
                <h2 className="font-bold text-slate-800">Passwort für Mobile App setzen</h2>
              </div>
              <button onClick={() => setPwdModalOpen(false)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-900">
                <p className="font-semibold mb-1">{data?.user?.name || ''} — {data?.user?.email}</p>
                <p>Setze ein Passwort, damit der Fahrer sich in der Mobile App einloggen kann (z.B. nach Anmeldung via Google ohne Passwort).</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Neues Passwort</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newPwd}
                    onChange={(e) => { setNewPwd(e.target.value); setPwdSaved(false); }}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:border-indigo-500 outline-none"
                    placeholder="Passwort"
                    data-testid="set-password-input"
                  />
                  <button onClick={() => { setNewPwd(generatePwd()); setPwdSaved(false); }} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm" title="Neu generieren" data-testid="regen-password-btn">↻</button>
                  <button onClick={copyPwd} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm" title="Kopieren" data-testid="copy-password-btn"><Copy size={14} /></button>
                </div>
              </div>
              {pwdSaved && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="password-saved-msg">
                  ✓ Passwort gespeichert. Bitte sicher an <b>{data?.user?.email}</b> übermitteln (WhatsApp/SMS empfohlen).
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={savePassword}
                  disabled={pwdSaving || !newPwd}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                  data-testid="save-password-btn"
                >
                  {pwdSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Passwort speichern
                </button>
                <button onClick={() => setPwdModalOpen(false)} className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium">Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ===== Personal Documents Section with Upload + Expiry =====
function PersonalDocsSection({ docs, driverId }) {
  const [expiries, setExpiries] = React.useState([]);
  const [uploading, setUploading] = React.useState(null);
  const [savingExpiry, setSavingExpiry] = React.useState(null);
  const [lightbox, setLightbox] = React.useState(null); // { src, label }

  React.useEffect(() => { if (driverId) loadExpiries(); }, [driverId]);

  const loadExpiries = async () => {
    try { const r = await api.get(`/admin/driver/${driverId}/document-expiries`); setExpiries(r.data || []); } catch {}
  };

  const getExpiry = (docType) => (expiries.find(e => e.doc_key === docType)?.expiry_date || '').slice(0, 10);

  const handleUpload = (docType, useCamera = false) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*,.pdf';
    if (useCamera) input.capture = 'environment';
    input.onchange = async (e) => {
      const file = e.target.files[0]; if (!file) return;
      setUploading(docType);
      const reader = new FileReader();
      reader.onload = async () => {
        try { await api.put(`/admin/driver/${driverId}/document`, { doc_type: docType, file_data: reader.result }); window.location.reload(); }
        catch { alert('Upload fehlgeschlagen'); }
        finally { setUploading(null); }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleDelete = async (docType) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    try {
      await api.put(`/admin/driver/${driverId}/document`, { doc_type: docType, file_data: '' });
      window.location.reload();
    } catch { alert('Löschen fehlgeschlagen'); }
  };

  const handleView = (val, label) => {
    // Use in-page lightbox instead of window.open — data: URLs are blocked
    // by modern browsers for security reasons in new tabs.
    setLightbox({ src: val, label });
  };

  const handleExpiry = async (docType, date) => {
    setSavingExpiry(docType);
    try { await api.put(`/admin/driver/${driverId}/document-expiry`, { doc_type: docType, expiry_date: date }); loadExpiries(); }
    catch { alert('Fehler'); }
    finally { setSavingExpiry(null); }
  };

  const PERSONAL_DOCS = [
    { key: 'id_passport', label: 'Personalausweis / Pass (Pflicht)' },
    { key: 'selfie_with_license', label: 'Selfie mit Führerschein' },
    { key: 'transport_license', label: 'Personenbeförderungsschein' },
    { key: 'criminal_record', label: 'Strafregisterauszug (optional)' },
  ];

  return (
    <div className="mt-6" data-testid="personal-docs-section">
      <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"><FileText size={16} /> Persönliche Dokumente</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PERSONAL_DOCS.map(doc => {
          const val = docs[doc.key];
          const hasDoc = val && val !== 'pending' && val !== '';
          const isImage = hasDoc && (val.startsWith('data:') || val.startsWith('http'));
          const expiryDate = getExpiry(doc.key);
          const isExpired = expiryDate && new Date(expiryDate) < new Date();
          return (
            <div key={doc.key} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`doc-${doc.key}`}>
              <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">{doc.label}</span>
                <span className={`text-xs font-medium ${hasDoc ? 'text-emerald-600' : 'text-slate-300'}`}>{hasDoc ? 'Vorhanden' : 'Fehlt'}</span>
              </div>
              {isImage ? (
                <div className="h-32 relative cursor-pointer" onClick={() => handleView(val, doc.label)}>
                  <img src={val} alt={doc.label} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center bg-slate-50"><Image size={20} className="text-slate-200" /></div>
              )}
              {/* Action Buttons */}
              <div className="flex border-t border-slate-100">
                {hasDoc && (
                  <button onClick={() => handleView(val, doc.label)} className="flex-1 flex items-center justify-center gap-1 py-2 text-blue-600 hover:bg-blue-50 text-[10px] font-medium transition-colors" data-testid={`view-${doc.key}`}>
                    <Eye size={11} /> Anzeigen
                  </button>
                )}
                {hasDoc && (
                  <button onClick={() => handleDelete(doc.key)} className="flex-1 flex items-center justify-center gap-1 py-2 text-red-500 hover:bg-red-50 text-[10px] font-medium border-l border-slate-100 transition-colors" data-testid={`delete-${doc.key}`}>
                    <Trash2 size={11} /> Löschen
                  </button>
                )}
                <button onClick={() => handleUpload(doc.key)} disabled={uploading === doc.key} className="flex-1 flex items-center justify-center gap-1 py-2 text-orange-500 hover:bg-orange-50 text-[10px] font-medium border-l border-slate-100 transition-colors" data-testid={`upload-${doc.key}`}>
                  {uploading === doc.key ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Lokal
                </button>
                <button onClick={() => handleUpload(doc.key, true)} disabled={uploading === doc.key} className="flex-1 flex items-center justify-center gap-1 py-2 text-slate-500 hover:bg-slate-100 text-[10px] font-medium border-l border-slate-100 transition-colors" data-testid={`camera-${doc.key}`}>
                  <Camera size={11} /> Kamera
                </button>
              </div>
              <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                <div className="flex items-center gap-1.5">
                  <Calendar size={10} className={isExpired ? 'text-red-500' : 'text-slate-400'} />
                  <span className="text-[10px] text-slate-500">Gültig bis:</span>
                  <input type="date" value={expiryDate} onChange={e => handleExpiry(doc.key, e.target.value)}
                    className={`text-[10px] px-1 py-0.5 border rounded w-28 ${isExpired ? 'border-red-300 text-red-600 bg-red-50' : 'border-slate-200 text-slate-600'}`}
                    data-testid={`expiry-${doc.key}`} />
                  {savingExpiry === doc.key && <Loader2 size={10} className="animate-spin text-orange-500" />}
                </div>
                {isExpired && <p className="text-[10px] text-red-500 mt-0.5 font-medium">Abgelaufen!</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="personal-doc-lightbox"
        >
          <div className="relative max-w-5xl w-full max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 text-white">
              <h3 className="text-base font-semibold">{lightbox.label}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={lightbox.src}
                  download={`${lightbox.label.replace(/\s+/g, '_')}.jpg`}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
                  title="Herunterladen"
                  onClick={e => e.stopPropagation()}
                  data-testid="personal-lightbox-download"
                >
                  <Download size={18} />
                </a>
                <button
                  onClick={() => setLightbox(null)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
                  title="Schließen"
                  data-testid="personal-lightbox-close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {lightbox.src?.startsWith('data:application/pdf') || lightbox.src?.toLowerCase().endsWith('.pdf') ? (
              <iframe src={lightbox.src} title={lightbox.label} className="w-full h-[85vh] rounded-lg bg-white" />
            ) : (
              <img
                src={lightbox.src}
                alt={lightbox.label}
                className="w-full max-h-[85vh] object-contain rounded-lg bg-white"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Driver Email Tab =====
function DriverEmailTab({ driverId, driverName, driverEmail, driverLanguage, docs, vehicles }) {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [selectedDocs, setSelectedDocs] = React.useState([]);
  const [customMsg, setCustomMsg] = React.useState('');
  const [emailLang, setEmailLang] = React.useState(driverLanguage || 'de');

  const LANGS = [
    { code: 'de', label: 'Deutsch' }, { code: 'en', label: 'English' }, { code: 'ar', label: 'العربية' },
    { code: 'fr', label: 'Français' }, { code: 'it', label: 'Italiano' }, { code: 'es', label: 'Español' },
    { code: 'tr', label: 'Türkçe' }, { code: 'ru', label: 'Русский' }, { code: 'pt', label: 'Português' },
    { code: 'nl', label: 'Nederlands' }, { code: 'zh', label: '中文' }, { code: 'sv', label: 'Svenska' },
  ];

  const TEMPLATES = {
    de: { subject: 'Fehlende Dokumente - 366Transfer', greeting: 'Hallo', body: 'Folgende Dokumente fehlen noch in Ihrem Profil:', cta: 'Dokumente hochladen', footer: 'Bitte laden Sie die fehlenden Dokumente so bald wie möglich hoch.' },
    en: { subject: 'Missing Documents - 366Transfer', greeting: 'Hello', body: 'The following documents are still missing from your profile:', cta: 'Upload Documents', footer: 'Please upload the missing documents as soon as possible.' },
    ar: { subject: 'مستندات مفقودة - 366Transfer', greeting: 'مرحبا', body: 'المستندات التالية لا تزال مفقودة من ملفك الشخصي:', cta: 'تحميل المستندات', footer: 'يرجى تحميل المستندات المفقودة في أقرب وقت ممكن.' },
    fr: { subject: 'Documents manquants - 366Transfer', greeting: 'Bonjour', body: 'Les documents suivants manquent encore à votre profil:', cta: 'Télécharger les documents', footer: 'Veuillez télécharger les documents manquants dès que possible.' },
    it: { subject: 'Documenti mancanti - 366Transfer', greeting: 'Ciao', body: 'I seguenti documenti mancano ancora dal tuo profilo:', cta: 'Carica documenti', footer: 'Si prega di caricare i documenti mancanti il prima possibile.' },
    es: { subject: 'Documentos faltantes - 366Transfer', greeting: 'Hola', body: 'Los siguientes documentos faltan en su perfil:', cta: 'Subir documentos', footer: 'Por favor suba los documentos faltantes lo antes posible.' },
    tr: { subject: 'Eksik Belgeler - 366Transfer', greeting: 'Merhaba', body: 'Profilinizde aşağıdaki belgeler hala eksik:', cta: 'Belgeleri yükle', footer: 'Lütfen eksik belgeleri en kısa sürede yükleyin.' },
    ru: { subject: 'Недостающие документы - 366Transfer', greeting: 'Здравствуйте', body: 'В вашем профиле отсутствуют следующие документы:', cta: 'Загрузить документы', footer: 'Пожалуйста, загрузите недостающие документы как можно скорее.' },
    pt: { subject: 'Documentos em falta - 366Transfer', greeting: 'Olá', body: 'Os seguintes documentos ainda faltam no seu perfil:', cta: 'Carregar documentos', footer: 'Por favor carregue os documentos em falta o mais rápido possível.' },
    nl: { subject: 'Ontbrekende documenten - 366Transfer', greeting: 'Hallo', body: 'De volgende documenten ontbreken nog in uw profiel:', cta: 'Documenten uploaden', footer: 'Upload de ontbrekende documenten zo snel mogelijk.' },
    zh: { subject: '缺少文件 - 366Transfer', greeting: '你好', body: '以下文件仍然缺少：', cta: '上传文件', footer: '请尽快上传缺少的文件。' },
    sv: { subject: 'Saknade dokument - 366Transfer', greeting: 'Hej', body: 'Följande dokument saknas fortfarande i din profil:', cta: 'Ladda upp dokument', footer: 'Vänligen ladda upp de saknade dokumenten så snart som möjligt.' },
  };

  // Document name translations per language
  const DOC_LABELS = {
    de: { id_passport: 'Personalausweis / Pass', selfie_with_license: 'Selfie mit Führerschein', transport_license: 'Personenbeförderungsschein', criminal_record: 'Strafregisterauszug', vehicle_registration: 'Fahrzeugschein', vehicle_photo: 'Fahrzeugfoto', insurance: 'Versicherung' },
    en: { id_passport: 'ID Card / Passport', selfie_with_license: 'Selfie with Driving License', transport_license: 'Passenger Transport License', criminal_record: 'Criminal Record Extract', vehicle_registration: 'Vehicle Registration', vehicle_photo: 'Vehicle Photo', insurance: 'Insurance' },
    ar: { id_passport: 'بطاقة الهوية / جواز السفر', selfie_with_license: 'صورة سيلفي مع رخصة القيادة', transport_license: 'رخصة نقل الركاب', criminal_record: 'صحيفة الحالة الجنائية', vehicle_registration: 'رخصة المركبة', vehicle_photo: 'صورة المركبة', insurance: 'التأمين' },
    fr: { id_passport: "Carte d'identité / Passeport", selfie_with_license: 'Selfie avec permis de conduire', transport_license: 'Licence de transport de passagers', criminal_record: 'Extrait de casier judiciaire', vehicle_registration: 'Carte grise', vehicle_photo: 'Photo du véhicule', insurance: 'Assurance' },
    it: { id_passport: "Carta d'identità / Passaporto", selfie_with_license: 'Selfie con patente di guida', transport_license: 'Licenza trasporto passeggeri', criminal_record: 'Estratto del casellario giudiziale', vehicle_registration: 'Libretto di circolazione', vehicle_photo: 'Foto del veicolo', insurance: 'Assicurazione' },
    es: { id_passport: 'DNI / Pasaporte', selfie_with_license: 'Selfie con permiso de conducir', transport_license: 'Licencia de transporte de pasajeros', criminal_record: 'Certificado de antecedentes penales', vehicle_registration: 'Permiso de circulación', vehicle_photo: 'Foto del vehículo', insurance: 'Seguro' },
    tr: { id_passport: 'Kimlik / Pasaport', selfie_with_license: 'Ehliyet ile selfie', transport_license: 'Yolcu taşıma belgesi', criminal_record: 'Adli sicil kaydı', vehicle_registration: 'Araç ruhsatı', vehicle_photo: 'Araç fotoğrafı', insurance: 'Sigorta' },
    ru: { id_passport: 'Удостоверение / Паспорт', selfie_with_license: 'Селфи с водительским удостоверением', transport_license: 'Лицензия на перевозку пассажиров', criminal_record: 'Справка о несудимости', vehicle_registration: 'Свидетельство о регистрации ТС', vehicle_photo: 'Фото автомобиля', insurance: 'Страховка' },
    pt: { id_passport: 'Bilhete de identidade / Passaporte', selfie_with_license: 'Selfie com carta de condução', transport_license: 'Licença de transporte de passageiros', criminal_record: 'Certificado de registo criminal', vehicle_registration: 'Documento do veículo', vehicle_photo: 'Foto do veículo', insurance: 'Seguro' },
    nl: { id_passport: 'ID-kaart / Paspoort', selfie_with_license: 'Selfie met rijbewijs', transport_license: 'Personenvervoervergunning', criminal_record: 'Uittreksel strafregister', vehicle_registration: 'Kentekenbewijs', vehicle_photo: 'Voertuigfoto', insurance: 'Verzekering' },
    zh: { id_passport: '身份证 / 护照', selfie_with_license: '与驾照的自拍', transport_license: '客运执照', criminal_record: '无犯罪记录证明', vehicle_registration: '行驶证', vehicle_photo: '车辆照片', insurance: '保险' },
    sv: { id_passport: 'ID-kort / Pass', selfie_with_license: 'Selfie med körkort', transport_license: 'Persontransporttillstånd', criminal_record: 'Belastningsregisterutdrag', vehicle_registration: 'Registreringsbevis', vehicle_photo: 'Fordonsfoto', insurance: 'Försäkring' },
  };
  const dl = DOC_LABELS[emailLang] || DOC_LABELS.de;

  const allDocs = [
    { key: 'id_passport', label: dl.id_passport, has: !!(docs?.id_passport) },
    { key: 'selfie_with_license', label: dl.selfie_with_license, has: !!(docs?.selfie_with_license) },
    { key: 'transport_license', label: dl.transport_license, has: !!(docs?.transport_license) },
    { key: 'criminal_record', label: dl.criminal_record, has: !!(docs?.criminal_record) },
  ];
  (vehicles || []).forEach(v => {
    const vd = v.documents || {};
    const vehicleName = `${v.make} ${v.model}`;
    allDocs.push({ key: `vehicle_registration_${v.id}`, label: `${dl.vehicle_registration} - ${vehicleName}`, has: !!vd.vehicle_registration });
    allDocs.push({ key: `vehicle_photo_${v.id}`, label: `${dl.vehicle_photo} - ${vehicleName}`, has: !!vd.vehicle_photo });
    allDocs.push({ key: `insurance_${v.id}`, label: `${dl.insurance} - ${vehicleName}`, has: !!vd.insurance });
  });

  const missingDocs = allDocs.filter(d => !d.has);
  const toggleDoc = (key) => setSelectedDocs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const selectAllMissing = () => setSelectedDocs(missingDocs.map(d => d.key));

  const handleSend = async () => {
    if (selectedDocs.length === 0) { alert('Bitte wählen Sie mindestens ein Dokument'); return; }
    setSending(true); setResult(null);
    try {
      const tpl = TEMPLATES[emailLang] || TEMPLATES.de;
      const dir = emailLang === 'ar' ? 'rtl' : 'ltr';
      const docLabels = selectedDocs.map(k => allDocs.find(d => d.key === k)?.label || k);
      const html = `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;direction:${dir}">
        <div style="text-align:center;margin-bottom:20px;"><span style="font-size:28px;font-weight:800;color:#f97316;">366</span><span style="font-size:18px;font-weight:600;color:#1e293b;">Transfer</span></div>
        <h2 style="color:#1e293b;">${tpl.subject}</h2>
        <p>${tpl.greeting} ${driverName},</p>
        <p>${tpl.body}</p>
        <ul style="color:#334155;">${docLabels.map(l => `<li style="padding:4px 0;"><b>${l}</b></li>`).join('')}</ul>
        ${customMsg ? `<p style="margin-top:16px;padding:12px;background:#f8fafc;border-radius:8px;color:#334155;">${customMsg}</p>` : ''}
        <p style="margin-top:12px;color:#475569;">${tpl.footer}</p>
        <div style="text-align:center;margin:24px 0;"><a href="https://www.366transfer.com/driver-app" style="background:#f97316;color:white;padding:12px 32px;border-radius:8px;text-decoration:none;font-weight:600;">${tpl.cta}</a></div>
        <hr style="border:none;border-top:1px solid #e2e8f0;margin:20px 0;"><p style="color:#94a3b8;font-size:12px;text-align:center;">© 2026 366Transfer OU</p></div>`;
      await api.post('/admin/send-email', { recipients: 'custom', custom_emails: [driverEmail], subject: tpl.subject, message: html });
      setResult({ success: true, msg: `E-Mail an ${driverEmail} gesendet (${LANGS.find(l=>l.code===emailLang)?.label})` });
    } catch (e) { setResult({ success: false, msg: e.response?.data?.detail || 'Fehler' }); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="driver-email-tab">
      <h2 className="text-lg font-bold text-slate-800">E-Mail an Fahrer senden</h2>
      <div className="bg-slate-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 mb-1">Empfänger</p>
            <p className="text-sm font-medium text-slate-800">{driverName} ({driverEmail})</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">E-Mail Sprache</p>
            <select value={emailLang} onChange={e => setEmailLang(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="email-lang-select">
              {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-500 uppercase">Fehlende Dokumente auswählen</p>
          {missingDocs.length > 0 && <button onClick={selectAllMissing} className="text-xs text-orange-500 hover:underline" data-testid="select-all-missing">Alle fehlenden wählen ({missingDocs.length})</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {allDocs.map(d => (
            <label key={d.key} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${selectedDocs.includes(d.key) ? 'border-orange-300 bg-orange-50' : 'border-slate-200'}`}>
              <input type="checkbox" checked={selectedDocs.includes(d.key)} onChange={() => toggleDoc(d.key)} className="w-3.5 h-3.5 rounded border-slate-300 text-orange-500" />
              <span className={d.has ? 'text-slate-400 line-through' : 'text-slate-700'}>{d.label}</span>
              {!d.has && <span className="text-[10px] text-red-500 font-medium ml-auto">Fehlt</span>}
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Zusätzliche Nachricht (optional)</p>
        <textarea value={customMsg} onChange={e => setCustomMsg(e.target.value)} rows={3} placeholder="Optionale Nachricht an den Fahrer..."
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" data-testid="driver-email-custom-msg" />
      </div>
      {result && <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{result.msg}</div>}
      <button onClick={handleSend} disabled={sending || selectedDocs.length === 0}
        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="send-driver-email-btn">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} E-Mail senden
      </button>
    </div>
  );
}

// ===== Driver SMS Tab =====
function DriverSmsTab({ driverName, driverPhone }) {
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true); setResult(null);
    try {
      await api.post('/admin/send-sms', { phone: driverPhone, message: message.trim() });
      setResult({ success: true, msg: `SMS an ${driverPhone} gesendet` });
      setMessage('');
    } catch (e) { setResult({ success: false, msg: e.response?.data?.detail || 'Fehler' }); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5" data-testid="driver-sms-tab">
      <h2 className="text-lg font-bold text-slate-800">SMS an Fahrer senden</h2>
      <div className="bg-slate-50 rounded-xl p-4">
        <p className="text-xs text-slate-500 mb-1">Empfänger</p>
        <p className="text-sm font-medium text-slate-800">{driverName}</p>
        <p className="text-sm text-slate-600 mt-0.5 flex items-center gap-1"><Phone size={12} /> {driverPhone || 'Keine Telefonnummer'}</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Nachricht</p>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="SMS-Nachricht eingeben..."
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" data-testid="driver-sms-message" />
        <p className="text-xs text-slate-400 mt-1">{message.length}/160 Zeichen</p>
      </div>
      {result && <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{result.msg}</div>}
      <button onClick={handleSend} disabled={sending || !message.trim() || !driverPhone}
        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50" data-testid="send-driver-sms-btn">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />} SMS senden
      </button>
    </div>
  );
}
